import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import api from "../lib/api";
import InquiryForm from "../components/InquiryForm";
import SmartImage from "../components/SmartImage";
import SEO from "../lib/SEO";

export default function FlavourDetail() {
    const { slug } = useParams();
    const [flavour, setFlavour] = useState(null);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        setNotFound(false);
        setFlavour(null);
        api.get(`/flavours/${slug}`)
            .then((r) => setFlavour(r.data))
            .catch(() => setNotFound(true));
    }, [slug]);

    if (notFound) {
        return (
            <main className="pt-32 pb-20 max-w-3xl mx-auto px-4 text-center" data-testid="flavour-notfound">
                <h2 className="font-display text-3xl mb-4">Flavour not found</h2>
                <Link to="/flavours" className="btn-pink mt-4">Back to flavours</Link>
            </main>
        );
    }

    if (!flavour) {
        return <main className="pt-32 pb-20 max-w-3xl mx-auto px-4 text-center text-ink-soft" data-testid="flavour-loading">Loading…</main>;
    }

    return (
        <main data-testid="flavour-detail-page">
            <SEO
                title={`${flavour.name} — Cake Central`}
                description={flavour.description}
                image={flavour.image_url}
                path={`/flavours/${flavour.slug}`}
                jsonLd={{
                    "@context": "https://schema.org",
                    "@type": "Product",
                    name: flavour.name,
                    image: flavour.image_url,
                    description: flavour.description,
                    brand: { "@type": "Brand", name: "Cake Central" },
                }}
            />

            <section className="pt-24 md:pt-[10rem] pb-16 bg-[#FFF7FB]">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <Link to="/flavours" className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-ink-soft hover:text-blush mb-8 transition-colors" data-testid="back-to-flavours">
                        <ArrowLeft size={14} /> Back to flavours
                    </Link>

                    <div className="grid md:grid-cols-2 gap-10 md:gap-16">
                        <div>
                            <div className="aspect-[4/5] overflow-hidden">
                                <SmartImage src={flavour.image_url} alt={flavour.name} className="w-full h-full object-cover" />
                            </div>
                        </div>
                        <div>
                            <p className="text-xs tracking-[0.3em] uppercase text-ink-soft mb-3">
                                {flavour.section === "gourmet" ? "Gourmet Flavour" : "Standard Flavour"}
                            </p>
                            <h1 className="font-display text-4xl md:text-5xl text-ink mb-5 leading-none" data-testid="flavour-title">
                                {flavour.name}
                            </h1>
                            <p className="text-ink-soft leading-relaxed mb-8 whitespace-pre-line">
                                {flavour.description}
                            </p>

                            {/* Info section — richer write-up about the flavour, admin-editable */}
                            {flavour.long_description && (
                                <div className="mb-8">
                                    <p className="text-xs tracking-[0.3em] uppercase text-ink-soft mb-3">About this flavour</p>
                                    <p className="text-ink-soft leading-relaxed whitespace-pre-line" data-testid="flavour-long-description">
                                        {flavour.long_description}
                                    </p>
                                </div>
                            )}

                            <div className="h-px bg-[#EAE3DE] mb-8" />
                            <p className="text-xs tracking-[0.3em] uppercase text-ink-soft mb-4">Enquire about this flavour</p>
                            <InquiryForm flavour={flavour} compact />
                        </div>
                    </div>
                </div>
            </section>
        </main>
    );
}
